import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import { addCartItem, logOut, removeCartItem, updateCartItemPortion, updateCartItemQuantity } from '../redux/actions/action-creator';
import { ProductCarouselSkeleton } from "./SkeletonLoader";
import CartSegmentWarningModal from './CartSegmentWarningModal';
import GuestLoginRequiredModal from './GuestLoginRequiredModal';
import ProductImage from './common/ProductImage';
import ProductPortionModal from './ProductPortionModal';
import { getActiveCartStoreName, hasCartSegmentMismatch } from '../utils/cartSegmentGuard';
import { getMarketMetadata } from '../utils/locationDefaults';
import { getProductDetailLink } from '../utils/productDetailNavigation';
import {
    getProductVariantId, getProductUnit,
    getProductPrice, getOriginalPrice, getProductPortions, hasPrice,
} from '../utils/productOptions';
import './newarrivalone.css';

const getCellContents = (data) => {
    const candidates = [
        data?.cell_contents,
        data?.response_data?.[0]?.cell_contents,
        data?.response_data,
        data?.data?.cell_contents,
        data?.data?.response_data?.[0]?.cell_contents,
        data?.data?.response_data,
        data?.data?.products,
        data?.data?.items,
        data?.products,
        data?.items,
        data?.data,
        data,
    ];

    return candidates.find((item) => Array.isArray(item)) || [];
};

const getSectionTitle = (data, fallback = "Related Products") => {
    const candidates = [
        data,
        data?.response_data?.[0],
        data?.data,
        data?.data?.response_data?.[0],
    ];

    for (const item of candidates) {
        const title =
            item?.cell_title_text ||
            item?.title ||
            item?.section_title ||
            item?.sectionTitle ||
            item?.heading ||
            item?.name;

        if (title) return title;
    }

    return fallback;
};

const NewArrivalOne = ({ recentlybrowse, loading = false, cartContext = {}, productDetailContext = {}, onAddToCart }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userData = {}, user_location, country_id } = useSelector((state) => state.auth);
    const cartState = useSelector((state) => state.cart || {});
    const storesData = useSelector((state) => state.common?.storesData);
    const cartItems = cartState?.cartItems || [];
    const [showGuestModal, setShowGuestModal] = useState(false);
    const [pendingCartItem, setPendingCartItem] = useState(null);
    const [selectedPortions, setSelectedPortions] = useState({});
    const [portionPicker, setPortionPicker] = useState(null);
    const productItems = getCellContents(recentlybrowse);
    const sectionTitle = getSectionTitle(recentlybrowse);
    const isGuest = Boolean(userData?.guest);
    const activeMarket = getMarketMetadata({ ...(user_location || {}), country_id });

    if (loading) {
        return <ProductCarouselSkeleton />;
    }

    if (!productItems.length) {
        return null;
    }

    const getSlidesToShow = (limit) => Math.max(1, Math.min(productItems.length, limit));

    const getProductId = (item, index) =>
        String(getProductVariantId(item) || item.product_id || `${item.title || item.name}-${index}`);

    const getProductTitle = (item) =>
        item.title ||
        item.name ||
        item.product_name ||
        item.product?.title ||
        item.product?.name ||
        item.product?.product_name ||
        'Product';

    const getProductImage = (item) =>
        item.image ||
        item.product_cover_image ||
        item.product_image ||
        item.productCoverImage ||
        item.image_url ||
        item.thumbnail ||
        item.cover_image ||
        item.product?.image ||
        item.product?.product_cover_image ||
        item.product?.product_image ||
        "";

    const getProductRating = (item) =>
        item.rating || item.avg_rating || item.product_rating || "4.5";

    const getProductCurrency = (item) =>
        item.currency || item.currency_code || item.currencyCode || activeMarket.currency;

    const getCartItem = (item, index) => {
        const productId = getProductId(item, index);
        return cartItems.find((cartItem) => String(cartItem.id) === productId);
    };

    const getSelectedPortion = (item, index) => {
        const portions = getProductPortions(item);
        const selectedUnit = selectedPortions[getProductId(item, index)];
        return portions.find((portion) => portion.portion === selectedUnit) || portions[0];
    };

    const submitPortion = async (portion) => {
        const { item, index } = portionPicker;
        const cartItem = getCartItem(item, index);
        if (cartItem && cartItem.portion !== portion.portion) {
            const response = await dispatch(updateCartItemPortion(cartItem.id, {
                portion: portion.portion,
                price: Number(getProductPrice(item, portion)),
            }, { cartId: cartContext.cart_id }));
            if (!response) {
                setPortionPicker(null);
                return;
            }
        }
        setSelectedPortions((current) => ({ ...current, [getProductId(item, index)]: portion.portion }));
        setPortionPicker(null);
    };

    const buildCartItem = (item, index) => {
        const productVariantId = getProductVariantId(item);
        const selectedPortion = getSelectedPortion(item, index);

        if (!productVariantId) return null;

        return {
            id: String(productVariantId),
            product_variant_id: productVariantId,
            name: getProductTitle(item),
            price: Number(getProductPrice(item, selectedPortion)),
            quantity: 1,
            image: getProductImage(item),
            currency: getProductCurrency(item),
            rating: item.rating,
            portion: selectedPortion?.portion || null,
            unit: selectedPortion ? "" : getProductUnit(item),
            merchant_id: item.merchant_id || item.merchantId || cartContext.merchant_id,
            empty_bottle_count: item.empty_bottle_count ?? item.emptyBottleCount ?? null,
            arr_option_id: item.arr_option_id ?? item.arrOptionId ?? null,
            segment_id: item.segment_id || item.segmentId || item.segment || cartContext.segment_id || cartContext.segmentId,
            business_segment_id:
                item.business_segment_id ||
                item.businessSegmentId ||
                item.store_id ||
                item.storeId ||
                cartContext.business_segment_id ||
                cartContext.businessSegmentId
        };
    };

    const increaseQuantity = (item, index) => {
        if (onAddToCart) {
            onAddToCart({ product: item, portion: getSelectedPortion(item, index)?.portion || null, quantity: 1 });
            return;
        }
        if (cartState.saveCartLoading || cartState.getCartLoading) return;
        if (isGuest) {
            setShowGuestModal(true);
            return;
        }

        const cartItem = buildCartItem(item, index);

        if (!cartItem) return;

        if (hasCartSegmentMismatch(cartState, cartItem)) {
            setPendingCartItem(cartItem);
            return;
        }

        dispatch(addCartItem(cartItem, { cartId: cartContext.cart_id }));
    };

    const closeCartSegmentWarning = () => {
        setPendingCartItem(null);
    };

    const confirmCartSegmentWarning = () => {
        if (!pendingCartItem || cartState.saveCartLoading || cartState.getCartLoading) {
            return;
        }

        dispatch(addCartItem(pendingCartItem, { replaceCart: true }));
        setPendingCartItem(null);
    };

    const handleGuestContinue = async () => {
        setShowGuestModal(false);
        await dispatch(logOut());
        navigate('/login');
    };

    const decreaseQuantity = (item, index) => {
        const cartItem = getCartItem(item, index);

        if (!cartItem) {
            return;
        }

        if (Number(cartItem.quantity || 0) <= 1) {
            dispatch(removeCartItem(cartItem.id, { cartId: cartContext.cart_id }));
            return;
        }

        dispatch(updateCartItemQuantity(cartItem.id, Number(cartItem.quantity) - 1, { cartId: cartContext.cart_id }));
    };

    function SampleNextArrow(props) {
        const { className, onClick } = props;
        return (
            <button
                type="button" onClick={onClick}
                className={` ${className} slick-next slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
            >
                <i className="ph ph-caret-right" />
            </button>
        );
    }
    function SamplePrevArrow(props) {
        const { className, onClick } = props;

        return (

            <button
                type="button"
                onClick={onClick}
                className={`${className} slick-prev slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
            >
                <i className="ph ph-caret-left" />
            </button>
        );
    }
    const settings = {
        dots: false,
        arrows: true,
        infinite: productItems.length > 6,
        speed: 0,
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: 0,
        adaptiveHeight: false,
        centerMode: false,
        variableWidth: true,
        autoplay: false,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        responsive: [
            {
                breakpoint: 1599,
                settings: {
                    slidesToShow: getSlidesToShow(6),

                },
            },
            {
                breakpoint: 1399,
                settings: {
                    slidesToShow: getSlidesToShow(4),

                },
            },
            {
                breakpoint: 992,
                settings: {
                    slidesToShow: getSlidesToShow(3),

                },
            },
            {
                breakpoint: 575,
                settings: {
                    slidesToShow: getSlidesToShow(2),

                },
            },
            {
                breakpoint: 424,
                settings: {
                    slidesToShow: getSlidesToShow(2),

                },
            },

        ],
    };
    return (
        <>
            <section className="new-arrival py-20">
                <div className="container container-lg">
                    <div className="section-heading">
                        <h5 className="mb-0">{sectionTitle}</h5>
                    </div>
                <div className="new-arrival__slider arrow-style-two">
                  <Slider {...settings}>
                    {productItems.map((item, index) => {
                        const detailLink = getProductDetailLink({ ...item, currency: getProductCurrency(item) }, { ...productDetailContext, cartContext });
                        const cartItem = getCartItem(item, index);
                        const quantity = Number(cartItem?.quantity || 0);
                        const portions = getProductPortions(item);
                        const selectedPortion = getSelectedPortion(item, index);
                        const originalPrice = getOriginalPrice(item, selectedPortion);
                        const productTitle = getProductTitle(item);

                        return (
                          <div className="new-arrival__slide-card" key={getProductId(item, index)}>
                            <div className="product-card new-arrival-product-card p-16 border border-gray-100 hover-border-main-four-600 rounded-16 position-relative transition-2">
                              <span className="product-card__badge new-arrival-product-card__badge bg-primary-600 px-8 py-4 text-sm text-white position-absolute">
                                Best Sale
                              </span>
                              <Link
                                {...detailLink}
                                className="product-card__thumb new-arrival-product-card__thumb flex-center rounded-8 position-relative"
                              >
                                {item.image_crop === "top" ? (
                                  <span className="new-arrival-product-card__cropped-image">
                                    <ProductImage src={getProductImage(item)} alt={productTitle} data-image-crop="top" />
                                  </span>
                                ) : (
                                  <ProductImage src={getProductImage(item)} alt={productTitle} className="w-100 h-100" />
                                )}
                                <span className="new-arrival-product-card__favorite" aria-hidden="true">
                                  <i className="ph-fill ph-heart" />
                                </span>
                                <span className="new-arrival-product-card__rating-badge">
                                  {getProductRating(item)} <i className="ph-fill ph-star" />
                                </span>
                                <span className="new-arrival-product-card__exclusive">Exclusive</span>
                              </Link>
                              <div className="product-card__content mt-20">
                                <h6 className="title text-lg mt-12 mb-8 product-cart-title">
                                  <Link
                                    {...detailLink}
                                    className="link new-arrival-product-card__title-link"
                                    tabIndex={0}
                                  >
                                    {productTitle}
                                  </Link>
                                </h6>
                                <div className="product-card__price mb-10">
                                    <span className="text-heading text-md">
                                    {getProductCurrency(item)} {getProductPrice(item, selectedPortion)}
                                  </span>
                                  {hasPrice(originalPrice) && (
                                    <del className="new-arrival-product-card__original-price text-gray-400 text-md fw-semibold text-decoration-line-through">
                                      {getProductCurrency(item)} {originalPrice}
                                    </del>
                                  )}
                                </div>
                                <div className="new-arrival-product-card__unit">
                                  {portions.length > 1 ? (
                                    <button
                                      type="button"
                                      className="new-arrival-product-card__unit-picker"
                                      aria-label={`${productTitle} unit`}
                                      aria-haspopup="dialog"
                                      disabled={cartState.saveCartLoading}
                                      onClick={() => setPortionPicker({ item, index })}
                                    >
                                      <span>{selectedPortion.portion}</span>
                                      <i className="ph ph-caret-down" aria-hidden="true" />
                                    </button>
                                  ) : (
                                    <span className="new-arrival-product-card__unit-text">{selectedPortion?.portion || getProductUnit(item)}</span>
                                  )}
                                </div>
                                {quantity > 0 ? (
                                  <div className="new-arrival-product-card__quantity d-flex rounded-8 overflow-hidden border border-gray-100">
                                    <button
                                      type="button"
                                      className="quantity__minus border-0 border-end border-gray-100 flex-shrink-0 text-neutral-600 flex-center"
                                      onClick={() => decreaseQuantity(item, index)}
                                      disabled={cartState.saveCartLoading}
                                      aria-label={`Decrease ${productTitle} quantity`}
                                    >
                                      <i className="ph ph-minus" />
                                    </button>
                                    <span className="quantity__input flex-grow-1 border-0 text-center px-4 flex-center fw-semibold">
                                      {quantity}
                                    </span>
                                    <button
                                      type="button"
                                      className="quantity__plus border-0 border-start border-gray-100 flex-shrink-0 text-neutral-600 flex-center"
                                      onClick={() => increaseQuantity(item, index)}
                                      disabled={cartState.saveCartLoading}
                                      aria-label={`Increase ${productTitle} quantity`}
                                    >
                                      <i className="ph ph-plus" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className="product-card__cart btn bg-color-primary text-white hover-text-white py-11 px-24 rounded-8 flex-center gap-8 fw-medium"
                                    tabIndex={0}
                                    disabled={!getProductVariantId(item) || cartState.saveCartLoading}
                                    onClick={() => increaseQuantity(item, index)}
                                    aria-label={getProductVariantId(item) ? `Add ${productTitle} to basket` : `${productTitle} unavailable`}
                                  >
                                    <span className="product-card__cart-text">{getProductVariantId(item) ? "Add to Cart" : "Unavailable"}</span>
                                    <i className="ph ph-shopping-cart" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                    })}
                  </Slider>
                </div>
                {/* <div className="home-section-action">
                    <Link
                        to="/shop"
                        className="home-section-action__link text-sm fw-medium text-gray-700 hover-text-main-600 hover-text-decoration-underline"
                    >
                        View All Deals
                    </Link>
                </div> */}
            </div>
        </section>
        {portionPicker && (
            <ProductPortionModal
                portions={getProductPortions(portionPicker.item)}
                currency={getProductCurrency(portionPicker.item)}
                getPortionPrice={(portion) => getProductPrice(portionPicker.item, portion)}
                submitting={cartState.saveCartLoading}
                selectedPortion={getSelectedPortion(portionPicker.item, portionPicker.index)?.portion}
                onClose={() => setPortionPicker(null)}
                onSubmit={submitPortion}
            />
        )}
        <GuestLoginRequiredModal
            show={showGuestModal}
            onClose={() => setShowGuestModal(false)}
            onContinue={handleGuestContinue}
        />
        <CartSegmentWarningModal
            show={Boolean(pendingCartItem)}
            storeName={getActiveCartStoreName(cartState, storesData)}
            submitting={cartState.saveCartLoading || cartState.getCartLoading}
            onClose={closeCartSegmentWarning}
            onConfirm={confirmCartSegmentWarning}
        />
    </>
  );
}
export default NewArrivalOne;
